import { type Nullable } from '~/common/types';

export interface OutlookDragData {
  type: 'files' | 'outlook-link' | 'unknown-text' | 'empty';
  files?: File[];
  url?: Nullable<string>;
  itemId?: Nullable<string>;
  subject?: Nullable<string>;
  userEmail?: Nullable<string>;
  rawHtml?: Nullable<string>;
  rawText?: Nullable<string>;
  rawOutlookData?: { [key: string]: unknown } | unknown[];
}

type MultiMailData = {
  subject: Nullable<string>;
  itemId: Nullable<string>;
  userEmail: Nullable<string>;
  json: { [key: string]: unknown } | unknown[] | null;
};

const extractFromMultiMailData = (raw: string): MultiMailData => {
  try {
    const parsed = JSON.parse(raw);

    return {
      itemId: parsed.itemIds?.[0] ?? parsed.latestItemIds?.[0] ?? null,
      json: parsed,
      subject: parsed.subjects?.[0] ?? null,
      userEmail:
        parsed.itemData?.mailboxInfo?.mailboxSmtpAddress ??
        parsed.itemData?.mailboxInfo?.userIdentity ??
        null,
    };
  } catch {
    return { itemId: null, json: null, subject: null, userEmail: null };
  }
};

const extractUrl = (uriData: string, textData: string): Nullable<string> => {
  if (uriData) {
    return uriData.trim().split('\n')[0] ?? null;
  }

  if (textData && (textData.startsWith('http://') || textData.startsWith('https://'))) {
    return textData.trim();
  }

  return null;
};

const extractSubjectFromHtmlDocument = (
  doc: Document,
  anchor: HTMLAnchorElement | null,
): Nullable<string> => {
  if (anchor?.textContent) {
    return anchor.textContent.trim();
  }

  const text = doc.body?.textContent?.trim();

  return text && text.length < 200 ? text : null;
};

const extractFromHtml = (
  htmlData: string,
): { url: Nullable<string>; subject: Nullable<string> } => {
  try {
    const doc = new DOMParser().parseFromString(htmlData, 'text/html');
    const anchor = doc.querySelector('a');

    return {
      subject: extractSubjectFromHtmlDocument(doc, anchor),
      url: anchor?.href || null,
    };
  } catch {
    return { subject: null, url: null };
  }
};

const extractItemIdFromUrl = (url: string): Nullable<string> => {
  try {
    const parsedUrl = new URL(url);
    const itemParam = parsedUrl.searchParams.get('ItemID') || parsedUrl.searchParams.get('itemId');

    if (itemParam) {
      return itemParam;
    }

    if (!parsedUrl.pathname.includes('/mail/id/')) {
      return null;
    }

    const [, encodedId] = parsedUrl.pathname.split('/mail/id/');

    return encodedId ? decodeURIComponent(encodedId) : null;
  } catch {
    return null;
  }
};

const isOutlookSource = (multiMailData: string, url: Nullable<string>): boolean =>
  !!multiMailData ||
  !!url?.includes('outlook.') ||
  !!url?.includes('office365.com') ||
  !!url?.includes('office.com');

/**
 * Extracts Outlook web link metadata, message ID, or New Outlook proprietary drag types
 * from New Outlook / OWA drag events.
 */
export const parseOutlookDragEvent = (event: React.DragEvent | DragEvent): OutlookDragData => {
  const { dataTransfer } = event;
  if (!dataTransfer) {
    return { type: 'empty' };
  }

  // TEMP DEBUG - remove once we've confirmed what Safari/macOS actually exposes here.

  // 1. Native OS files (Classic Outlook, File Explorer)
  if (dataTransfer.files && dataTransfer.files.length > 0) {
    return {
      files: Array.from(dataTransfer.files),
      type: 'files',
    };
  }

  // 2. Check for New Outlook proprietary drag types - the row payload's DataTransfer type key
  // isn't fixed. New Outlook names it after what's being dragged (observed: 'maillistrow' for a
  // single message, 'multimaillistmessagerows' for a multi-select, 'multimaillistconversationrows'
  // for a conversation/thread row, 'maillistitempart' for an item part) but every variant seen so
  // far contains 'maillist', so match on that instead of enumerating every variant.
  const multiMailType = Array.from(dataTransfer.types).find((type) => /maillist/i.test(type));
  const multiMailData = multiMailType ? dataTransfer.getData(multiMailType) : '';
  const htmlData = dataTransfer.getData('text/html');
  const uriData = dataTransfer.getData('text/uri-list');
  const textData = dataTransfer.getData('text/plain');

  let extractedSubject: Nullable<string> = null;
  let extractedItemId: Nullable<string> = null;
  let extractedUserEmail: Nullable<string> = null;
  let parsedOutlookJson: { [key: string]: unknown } | unknown[] | null = null;

  if (multiMailData) {
    const fromMultiMail = extractFromMultiMailData(multiMailData);
    extractedSubject = fromMultiMail.subject;
    extractedItemId = fromMultiMail.itemId;
    extractedUserEmail = fromMultiMail.userEmail;
    parsedOutlookJson = fromMultiMail.json;
  }

  let extractedUrl: Nullable<string> = extractUrl(uriData, textData);

  if (htmlData) {
    const fromHtml = extractFromHtml(htmlData);

    if (!extractedUrl && fromHtml.url) {
      extractedUrl = fromHtml.url;
    }
    if (!extractedSubject && fromHtml.subject) {
      extractedSubject = fromHtml.subject;
    }
  }

  if (!extractedSubject && textData && !textData.startsWith('http')) {
    extractedSubject = (textData.split('\n')[0] ?? '').substring(0, 150);
  }

  if (!extractedItemId && extractedUrl) {
    extractedItemId = extractItemIdFromUrl(extractedUrl);
  }

  if (isOutlookSource(multiMailData, extractedUrl) || extractedItemId) {
    return {
      itemId: extractedItemId,
      rawHtml: htmlData || null,
      rawOutlookData: parsedOutlookJson || undefined,
      rawText: textData || null,
      subject: extractedSubject || 'New Outlook Email Reference',
      type: 'outlook-link',
      url: extractedUrl,
      userEmail: extractedUserEmail,
    };
  }

  if (extractedUrl || textData || htmlData) {
    return {
      rawHtml: htmlData || null,
      rawText: textData || null,
      subject: extractedSubject,
      type: 'unknown-text',
      url: extractedUrl,
    };
  }

  return { type: 'empty' };
};
